import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
  input,
  output,
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import {
  EventBus,
  PDFLinkService,
  PDFFindController,
  PDFViewer,
  GenericL10n,
} from 'pdfjs-dist/web/pdf_viewer.mjs';

@Component({
  selector: 'pdf-viewer',
  standalone: true,
  imports: [],
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss',
})
export class PdfViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly src = input<string>('');

  readonly currentPage = signal(1);
  readonly totalPages = signal(0);
  readonly loading = signal(true);
  readonly loadingProgress = signal(0);
  readonly loadingMessage = signal('Loading document…');
  readonly loadError = signal('');

  readonly pageChange = output<number>();
  readonly pagesLoaded = output<number>();
  readonly pageRendered = output<void>();
  readonly beyondPageWidth = output<boolean>();
  readonly isScrollable = output<boolean>();
  readonly scrolledToBottom = output<boolean>();

  @ViewChild('viewerContainer', { static: true })
  viewerContainerRef!: ElementRef<HTMLDivElement>;

  private eventBus!: EventBus;
  private linkService!: PDFLinkService;
  private findController!: PDFFindController;
  private pdfViewer!: PDFViewer;
  private pdfDocument: pdfjsLib.PDFDocumentProxy | null = null;
  private resizeHandler = () => this.onResize();
  private resizeObserver: ResizeObserver | null = null;

  // Touch gesture state
  private isPinching = false;
  private isDragging = false;
  private pinchStartDistance = 0;
  private pinchStartScale = 0;
  private pinchStartScrollLeft = 0;
  private pinchStartScrollTop = 0;
  private pinchMidX = 0;
  private pinchMidY = 0;
  private lastTouchX = 0;
  private lastTouchY = 0;
  private velocityX = 0;
  private velocityY = 0;
  private lastMoveTime = 0;
  private momentumId = 0;
  private isUserZoomed = false;
  private pageFitScale = 0;
  private pageWidthScale = 0;
  private _isBeyondPageWidth = false;
  private snapBackPending = false;
  private _isScrollable: boolean | null = null;
  private _isAtBottom = false;
  private scrollHandler = () => this.onScroll();

  constructor(private readonly ngZone: NgZone) {}

  ngOnInit(): void {
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdfjs/pdf.worker.mjs';

    this.eventBus = new EventBus();
    this.linkService = new PDFLinkService({ eventBus: this.eventBus });
    this.findController = new PDFFindController({
      eventBus: this.eventBus,
      linkService: this.linkService,
    });
    const l10n = new GenericL10n('en-US');

    this.pdfViewer = new PDFViewer({
      container: this.viewerContainerRef.nativeElement,
      eventBus: this.eventBus,
      linkService: this.linkService,
      findController: this.findController,
      l10n,
      textLayerMode: 2,
      annotationMode: 2,
    });

    this.linkService.setViewer(this.pdfViewer);
    this.pdfViewer.scrollMode = 3; // PAGE scroll mode

    // Hide viewer until page-fit is applied to avoid a flash of
    // full-size content at the default 1.0 scale.
    const viewerEl = this.viewerContainerRef.nativeElement.querySelector('#viewer') as HTMLElement;
    if (viewerEl) viewerEl.style.visibility = 'hidden';

    this.eventBus.on('pagesloaded', () => {
      this.pdfViewer.currentScaleValue = 'page-fit';
      this.pageFitScale = this.pdfViewer.currentScale;
      this.pdfViewer.currentScaleValue = 'page-width';
      this.pageWidthScale = this.pdfViewer.currentScale;
      this.checkBeyondPageWidth();
      if (viewerEl) viewerEl.style.visibility = '';
      const numPages = this.pdfDocument?.numPages ?? 0;
      this.totalPages.set(numPages);
      this.currentPage.set(1);
      this.loading.set(false);
      this.pagesLoaded.emit(numPages);
      this.pageChange.emit(1);
    });

    this.eventBus.on('pagechanging', (evt: { pageNumber: number }) => {
      this.currentPage.set(evt.pageNumber);
      this.pageChange.emit(evt.pageNumber);
      if (this._isAtBottom) {
        this._isAtBottom = false;
        this.scrolledToBottom.emit(false);
      }
      if (!this.isUserZoomed) {
        requestAnimationFrame(() => {
          if (!this.isUserZoomed) {
            this.pdfViewer.currentScaleValue = 'page-fit';
            this.pageFitScale = this.pdfViewer.currentScale;
            this.pdfViewer.currentScaleValue = 'page-width';
            this.pageWidthScale = this.pdfViewer.currentScale;
            this.checkBeyondPageWidth();
          }
        });
      }
    });

    this.eventBus.on('pagerendered', () => {
      this.pageRendered.emit();
      requestAnimationFrame(() => this.checkScrollable());
    });

    window.addEventListener('resize', this.resizeHandler);

    const container = this.viewerContainerRef.nativeElement;
    container.addEventListener('touchstart', this.onPinchStart, {
      passive: false,
    });
    container.addEventListener('touchmove', this.onPinchMove, {
      passive: false,
    });
    container.addEventListener('touchend', this.onPinchEnd);
    container.addEventListener('scroll', this.scrollHandler, { passive: true });

    const pdfSrc = this.src();
    if (pdfSrc) {
      this.loadPDF(pdfSrc);
    }
  }

  ngAfterViewInit(): void {
    const container = this.viewerContainerRef.nativeElement;
    // Initial layout: container may have 0 size on first paint (flex not yet computed).
    // Run resize after layout so pdf.js gets correct dimensions and avoids collapsed .page.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.onResize());
    });
    // When container gets real size (e.g. after parent flex layout), trigger resize so
    // reload shows correct size without needing user resize/navigation.
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.ngZone.run(() => this.onResize());
      });
    });
    this.resizeObserver.observe(container);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    window.removeEventListener('resize', this.resizeHandler);
    this.stopMomentum();
    const container = this.viewerContainerRef.nativeElement;
    container.removeEventListener('touchstart', this.onPinchStart);
    container.removeEventListener('touchmove', this.onPinchMove);
    container.removeEventListener('touchend', this.onPinchEnd);
    container.removeEventListener('scroll', this.scrollHandler);
    this.pdfDocument?.destroy();
  }

  async loadPDF(source: string | ArrayBuffer): Promise<void> {
    this.loading.set(true);
    this.loadingProgress.set(0);
    this.loadingMessage.set('Loading document…');
    this.loadError.set('');

    if (this.pdfDocument) {
      await this.pdfDocument.destroy();
      this.pdfDocument = null;
    }

    this.isUserZoomed = false;

    try {
      const loadingTask = pdfjsLib.getDocument({
        ...(typeof source === 'string' ? { url: source } : { data: source }),
        cMapUrl: 'pdfjs/cmaps/',
        cMapPacked: true,
        standardFontDataUrl: 'pdfjs/standard_fonts/',
        enableXfa: true,
      });

      loadingTask.onProgress = ({
        loaded,
        total,
      }: {
        loaded: number;
        total: number;
      }) => {
        if (total > 0) {
          const pct = Math.round((loaded / total) * 100);
          this.loadingProgress.set(pct);
          this.loadingMessage.set(`Loading… ${pct}%`);
        }
      };

      this.pdfDocument = await loadingTask.promise;
      this.pdfViewer.setDocument(this.pdfDocument);
      this.linkService.setDocument(this.pdfDocument, null);
      // totalPages, loading, pagesLoaded and pageChange are emitted from
      // eventBus 'pagesloaded' when the viewer has created the .page DOM elements.
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      this.loadError.set(message);
      this.loadingMessage.set(`Error: ${message}`);
      this.loadingProgress.set(0);
    }
  }

  goToPreviousPage(): void {
    if (this.pdfViewer.currentPageNumber > 1) {
      if (this.isUserZoomed) {
        this.isUserZoomed = false;
        this.pdfViewer.currentScaleValue = 'page-width';
        this.pageWidthScale = this.pdfViewer.currentScale;
        this.checkBeyondPageWidth();
      }
      this.pdfViewer.currentPageNumber--;
    }
  }

  goToNextPage(): void {
    if (
      this.pdfDocument &&
      this.pdfViewer.currentPageNumber < this.pdfDocument.numPages
    ) {
      if (this.isUserZoomed) {
        this.isUserZoomed = false;
        this.pdfViewer.currentScaleValue = 'page-width';
        this.pageWidthScale = this.pdfViewer.currentScale;
        this.checkBeyondPageWidth();
      }
      this.pdfViewer.currentPageNumber++;
    }
  }

  goToPage(pageNumber: number): void {
    if (this.pdfViewer && pageNumber >= 1 && pageNumber <= this.totalPages()) {
      if (this.isUserZoomed) {
        this.isUserZoomed = false;
        this.pdfViewer.currentScaleValue = 'page-width';
        this.pageWidthScale = this.pdfViewer.currentScale;
        this.checkBeyondPageWidth();
      }
      this.pdfViewer.currentPageNumber = pageNumber;
    }
  }

  get canGoPrevious(): boolean {
    return this.currentPage() > 1;
  }

  get canGoNext(): boolean {
    return this.currentPage() < this.totalPages();
  }

  private onPinchStart = (e: TouchEvent): void => {
    if (e.touches.length === 2) {
      e.preventDefault();
      this.isDragging = false;
      this.stopMomentum();
      this.isPinching = true;

      const container = this.viewerContainerRef.nativeElement;

      // Cancel any snap-back animation and settle at page-fit
      if (this.snapBackPending) {
        this.snapBackPending = false;
        const viewer = container.querySelector('#viewer') as HTMLElement;
        if (viewer) {
          viewer.style.transition = '';
          viewer.style.transform = '';
          viewer.style.transformOrigin = '';
        }
        this.isUserZoomed = false;
        this.pdfViewer.currentScaleValue = 'page-fit';
        this.pageFitScale = this.pdfViewer.currentScale;
        this.checkBeyondPageWidth();
      }

      this.pinchStartDistance = this.getTouchDistance(e.touches);
      this.pinchStartScale = this.pdfViewer.currentScale;

      const rect = container.getBoundingClientRect();
      this.pinchMidX =
        (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      this.pinchMidY =
        (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
      this.pinchStartScrollLeft = container.scrollLeft;
      this.pinchStartScrollTop = container.scrollTop;
    } else if (e.touches.length === 1 && !this.isPinching) {
      this.stopMomentum();
      this.isDragging = true;
      this.lastTouchX = e.touches[0].clientX;
      this.lastTouchY = e.touches[0].clientY;
      this.velocityX = 0;
      this.velocityY = 0;
      this.lastMoveTime = e.timeStamp;
    }
  };

  private onPinchMove = (e: TouchEvent): void => {
    if (this.isPinching && e.touches.length === 2) {
      e.preventDefault();

      // CSS transform for smooth visual — no pdf.js re-render
      let scaleFactor =
        this.getTouchDistance(e.touches) / this.pinchStartDistance;

      // Rubber-band: dampen zoom-out below page-fit
      if (this.pageFitScale > 0) {
        const effectiveScale = this.pinchStartScale * scaleFactor;
        if (effectiveScale < this.pageFitScale) {
          const overscroll = this.pageFitScale - effectiveScale;
          const dampened = this.pageFitScale - overscroll * 0.3;
          scaleFactor = dampened / this.pinchStartScale;
        }
      }

      const viewer = this.viewerContainerRef.nativeElement.querySelector(
        '#viewer'
      ) as HTMLElement;
      if (viewer) {
        const originX = this.pinchStartScrollLeft + this.pinchMidX - viewer.offsetLeft;
        const originY = this.pinchStartScrollTop + this.pinchMidY - viewer.offsetTop;
        viewer.style.transformOrigin = `${originX}px ${originY}px`;
        viewer.style.transform = `scale(${scaleFactor})`;
      }
    } else if (this.isDragging && e.touches.length === 1) {
      e.preventDefault();

      const container = this.viewerContainerRef.nativeElement;
      const dx = e.touches[0].clientX - this.lastTouchX;
      const dy = e.touches[0].clientY - this.lastTouchY;
      const dt = e.timeStamp - this.lastMoveTime;

      container.scrollLeft -= dx;
      container.scrollTop -= dy;

      // Track velocity for momentum (normalize to ~16ms frame)
      if (dt > 0) {
        this.velocityX = (dx / dt) * 16;
        this.velocityY = (dy / dt) * 16;
      }

      this.lastTouchX = e.touches[0].clientX;
      this.lastTouchY = e.touches[0].clientY;
      this.lastMoveTime = e.timeStamp;
    }
  };

  private onPinchEnd = (e: TouchEvent): void => {
    if (this.isPinching && e.touches.length < 2) {
      this.isPinching = false;

      const viewer = this.viewerContainerRef.nativeElement.querySelector(
        '#viewer'
      ) as HTMLElement;
      const container = this.viewerContainerRef.nativeElement;

      // Extract scale factor from CSS transform
      let scaleFactor = 1;
      if (viewer?.style.transform) {
        const match = viewer.style.transform.match(/scale\(([^)]+)\)/);
        if (match) scaleFactor = parseFloat(match[1]);
      }

      // Snap back to page-fit if zoomed out at or below the limit
      const effectiveScale = this.pinchStartScale * scaleFactor;
      if (this.pageFitScale > 0 && effectiveScale <= this.pageFitScale) {
        const targetFactor = this.pageFitScale / this.pinchStartScale;
        if (viewer) {
          viewer.style.transition = 'transform 200ms ease-out';
          viewer.style.transform = `scale(${targetFactor})`;
          this.snapBackPending = true;
          let settled = false;
          const settle = () => {
            if (settled || !this.snapBackPending) return;
            settled = true;
            this.snapBackPending = false;
            viewer.style.transition = '';
            viewer.style.transform = '';
            viewer.style.transformOrigin = '';
            this.isUserZoomed = false;
            this.pdfViewer.currentScaleValue = 'page-fit';
            this.pageFitScale = this.pdfViewer.currentScale;
            // Let layout settle (helps on mobile where transitionend timing can differ), then
            // run in NgZone so parent's OnPush change detection runs and nav/indicator reappear.
            const notifyBeyondWidth = () => {
              this.ngZone.run(() => {
                this._isBeyondPageWidth = false;
                this.beyondPageWidth.emit(false);
              });
            };
            requestAnimationFrame(() => {
              requestAnimationFrame(notifyBeyondWidth);
            });
          };
          viewer.addEventListener('transitionend', settle, { once: true });
          setTimeout(settle, 280);
        }
        return;
      }

      // Remove CSS transform
      if (viewer) {
        viewer.style.transform = '';
        viewer.style.transformOrigin = '';
      }

      // Measure page position BEFORE scale change.
      // The page has `margin: auto` centering, so its offsetLeft changes
      // when the scale changes (wider page = less margin).
      const pageEl = container.querySelector('.page') as HTMLElement;
      const oldPageLeft = pageEl ? pageEl.offsetLeft : 0;
      const oldPageTop = pageEl ? pageEl.offsetTop : 0;

      // Pinch midpoint relative to the page element (at old scale)
      const localX = this.pinchStartScrollLeft + this.pinchMidX - oldPageLeft;
      const localY = this.pinchStartScrollTop + this.pinchMidY - oldPageTop;

      // Bypass pdf.js's scrollPageIntoView during scale commit —
      // it repositions the page internally causing the "relocation" jump.
      this.isUserZoomed = true;
      const pv = this.pdfViewer as any;
      pv.scrollPageIntoView = () => {};
      const newScale = Math.max(0.1, Math.min(10, this.pinchStartScale * scaleFactor));
      pv.currentScale = newScale;
      delete pv.scrollPageIntoView; // restore prototype method
      this.checkBeyondPageWidth();

      // Measure page position AFTER scale change (reflow gives new margins)
      const newPageLeft = pageEl ? pageEl.offsetLeft : 0;
      const newPageTop = pageEl ? pageEl.offsetTop : 0;

      // The content point scaled within the page, plus the page's new offset
      const targetScrollLeft = newPageLeft + localX * scaleFactor - this.pinchMidX;
      const targetScrollTop = newPageTop + localY * scaleFactor - this.pinchMidY;

      container.scrollLeft = targetScrollLeft;
      container.scrollTop = targetScrollTop;

      // Pin scroll for a few frames — pdf.js async rendering/layout
      // may try to adjust scroll after the scale commit.
      let pinFrames = 5;
      const pinScroll = () => {
        container.scrollLeft = targetScrollLeft;
        container.scrollTop = targetScrollTop;
        if (--pinFrames > 0) {
          requestAnimationFrame(pinScroll);
        }
      };
      requestAnimationFrame(pinScroll);

      // If one finger remains, transition to dragging
      if (e.touches.length === 1) {
        this.isDragging = true;
        this.lastTouchX = e.touches[0].clientX;
        this.lastTouchY = e.touches[0].clientY;
        this.velocityX = 0;
        this.velocityY = 0;
        this.lastMoveTime = e.timeStamp;
      }
    }
    if (e.touches.length === 0) {
      if (this.isDragging) {
        this.isDragging = false;
        this.startMomentum();
      }
    }
  };

  private startMomentum(): void {
    const friction = 0.95;
    const minVelocity = 0.5;
    const container = this.viewerContainerRef.nativeElement;

    const animate = () => {
      if (
        Math.abs(this.velocityX) < minVelocity &&
        Math.abs(this.velocityY) < minVelocity
      ) {
        this.momentumId = 0;
        return;
      }
      container.scrollLeft -= this.velocityX;
      container.scrollTop -= this.velocityY;
      this.velocityX *= friction;
      this.velocityY *= friction;
      this.momentumId = requestAnimationFrame(animate);
    };
    this.momentumId = requestAnimationFrame(animate);
  }

  private stopMomentum(): void {
    if (this.momentumId) {
      cancelAnimationFrame(this.momentumId);
      this.momentumId = 0;
    }
  }

  private getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private onResize(): void {
    this.eventBus.dispatch('resize', { source: window });
    if (!this.pdfViewer) return;

    if (this.isUserZoomed) {
      // Measure page-width for the new container size, then restore the user's scale.
      const savedScale = this.pdfViewer.currentScale;
      const pv = this.pdfViewer as any;
      pv.scrollPageIntoView = () => {};
      this.pdfViewer.currentScaleValue = 'page-width';
      this.pageWidthScale = this.pdfViewer.currentScale;
      pv.currentScale = savedScale;
      delete pv.scrollPageIntoView;
    } else {
      this.pdfViewer.currentScaleValue = 'page-fit';
      this.pageFitScale = this.pdfViewer.currentScale;
      this.pdfViewer.currentScaleValue = 'page-width';
      this.pageWidthScale = this.pdfViewer.currentScale;
    }
    this.checkBeyondPageWidth();
    this.checkScrollable();
  }

  private onScroll(): void {
    const container = this.viewerContainerRef.nativeElement;
    const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
    if (atBottom !== this._isAtBottom) {
      this._isAtBottom = atBottom;
      this.scrolledToBottom.emit(atBottom);
    }
  }

  private checkScrollable(): void {
    const container = this.viewerContainerRef.nativeElement;
    const scrollable = container.scrollHeight > container.clientHeight;
    if (scrollable !== this._isScrollable) {
      this._isScrollable = scrollable;
      this.isScrollable.emit(scrollable);
    }
  }

  private checkBeyondPageWidth(): void {
    const beyond = this.pageWidthScale > 0 && this.pdfViewer.currentScale > this.pageWidthScale;
    if (beyond !== this._isBeyondPageWidth) {
      this._isBeyondPageWidth = beyond;
      this.beyondPageWidth.emit(beyond);
    }
  }
}
